import { useMemo, useRef } from "react";
import Konva from "konva";
import { Group, Rect, Text } from "react-konva";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectGroupEvaluationSummary,
  selectViewedEvaluationMode,
  selectViewedGroups,
  selectViewedRectangles,
} from "@store/selectors";
import { selectIsGradingViewActive } from "@store/selectors";
import {
  addGroupEdge,
  setGroupMeta,
  translateGroupMembers,
} from "@store/slices/workspaceSlice";
import {
  endDragGroup,
  openGroupContextMenu,
  setCoords,
  startDragGroup,
} from "@features/annogram/canvas/state/canvasAuxSlice";
import type {
  CanvasGroup,
  RectangleGeometry,
} from "@/types/canvasTypes";
import { rectsHaveIntersection } from "@shared/utils/utils";

const FRAME_PADDING = 12;
const FRAME_RADIUS = 8;
// Bumped from 22 to 28 so the label is comfortably tappable and the
// 13px text + 14px checkbox don't fight for vertical space.
const LABEL_HEIGHT = 28;
const LABEL_FONT_SIZE = 13;
// Theme-paired label pill colors. Each pair keeps text/background
// contrast well above WCAG AA (4.5:1) and the unchecked-checkbox
// stroke above the AA UI-component minimum (3:1):
//   light: slate-800 text on white pill   → ~14:1
//   dark:  slate-100 text on slate-800   → ~14:1
const LABEL_TEXT_FILL_LIGHT = "#1f2937";
const LABEL_TEXT_FILL_DARK = "#f1f5f9";
const LABEL_BG_LIGHT = "#ffffff";
const LABEL_BG_DARK = "#1f2937";
const TOGGLE_OFF_STROKE_LIGHT = "#475569"; // slate-600 on white  → ~7:1
const TOGGLE_OFF_STROKE_DARK = "#94a3b8"; // slate-400 on slate-800 → ~7:1
// Default frame stroke / fill (group-color when set). Holds on both
// themes — slate-600 has ~3:1 against white and ~3.3:1 against a
// near-black canvas, both above the AA UI-component threshold.
const DEFAULT_STROKE = "#475569";
const DEFAULT_FILL = "rgba(71, 85, 105, 0.06)";
// Standalone "active" color for the evaluate toggle — MUI primary
// blue, ~4.6:1 against white and ~5:1 against slate-800, comfortably
// above AA on both themes. Picked independently of group.color so a
// custom color choice doesn't regress the toggle's contrast.
const TOGGLE_ON_FILL = "#1976d2";
const CHECKBOX_SIZE = 14;
const CHECKBOX_LABEL_GAP = 6;
// Per-group result chip colors (next to the Evaluate checkbox). Chosen
// for WCAG AA contrast against white text on the chip fill:
//   error  #b91c1c → ~5.9:1
//   warn   #c2410c → ~5:1
//   ok     #15803d → ~4.5:1
const CHIP_ERROR_BG = "#b91c1c";
const CHIP_WARN_BG = "#c2410c";
const CHIP_OK_BG = "#15803d";
const CHIP_HEIGHT = 18;
const CHIP_MIN_WIDTH = 22;
const CHIP_GAP = 8;

export const groupBounds = (
  group: CanvasGroup,
  rectangles: { x: number; y: number; width: number; height: number }[]
): RectangleGeometry | null => {
  if (group.frame) return group.frame;
  const members = group.memberRectIndices
    .map((i) => rectangles[i])
    .filter((r): r is NonNullable<typeof r> => r !== undefined);
  if (members.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of members) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.width > maxX) maxX = r.x + r.width;
    if (r.y + r.height > maxY) maxY = r.y + r.height;
  }
  return {
    x: minX - FRAME_PADDING,
    y: minY - FRAME_PADDING,
    width: maxX - minX + FRAME_PADDING * 2,
    height: maxY - minY + FRAME_PADDING * 2,
  };
};

/**
 * Renders one labeled frame per CanvasGroup behind the main rect layer.
 * The frame is draggable; dragging dispatches translateGroupMembers
 * which moves every member rect (and the frame, if user-resized) in
 * parallel. Frames render readonly in grading view.
 *
 * Frame visuals: rounded outline + faint fill in the group's color
 * (defaults to a neutral slate). Label rendered as a small pill in the
 * top-left of the frame, showing the group name when set.
 */
const CanvasGroupFrame = () => {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(selectViewedGroups);
  const rectangles = useAppSelector(selectViewedRectangles);
  const readOnly = useAppSelector(selectIsGradingViewActive);
  const evaluationMode = useAppSelector(selectViewedEvaluationMode);
  const groupSummaries = useAppSelector(selectGroupEvaluationSummary);
  const themeState = useAppSelector((state) => state.theme.mode);
  const isDark = themeState === "dark";
  const labelBg = isDark ? LABEL_BG_DARK : LABEL_BG_LIGHT;
  const labelTextFill = isDark ? LABEL_TEXT_FILL_DARK : LABEL_TEXT_FILL_LIGHT;
  const toggleOffStroke = isDark
    ? TOGGLE_OFF_STROKE_DARK
    : TOGGLE_OFF_STROKE_LIGHT;
  // Tracks the Konva node's last-synced absolute position during a
  // drag. We dispatch incremental deltas on every dragMove so members
  // follow the frame in real time (rather than snap into place on
  // dragEnd). Only one frame is dragged at a time, so a single ref
  // suffices regardless of how many groups are rendered.
  const dragSyncRef = useRef<{ x: number; y: number } | null>(null);
  // Where the frame drag started, for the connect gesture's spring-back
  // (drop a frame onto a node or another frame -> edge + return home).
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);

  const framed = useMemo(
    () =>
      groups
        .map((g) => ({ group: g, bounds: groupBounds(g, rectangles) }))
        .filter(
          (entry): entry is { group: CanvasGroup; bounds: RectangleGeometry } =>
            entry.bounds !== null
        ),
    [groups, rectangles]
  );

  if (framed.length === 0) return null;

  return (
    <>
      {framed.map(({ group, bounds }) => {
        const stroke = group.color || DEFAULT_STROKE;
        const fill = group.color
          ? `${group.color}14` // ~8% alpha when the color is a hex like #RRGGBB
          : DEFAULT_FILL;
        // Anchor the MUI menu in screen coords (left/top of viewport).
        // The +4 mirrors the offset used by the rect/arrow menus so
        // menus feel placed consistently. Shared by the frame's
        // right-click and the label pill's click — the label is the
        // most discoverable click target for group actions.
        const openMenuAtPointer = (
          e: Konva.KonvaEventObject<MouseEvent | Event>
        ) => {
          if (readOnly) return;
          const stage = e.target.getStage();
          const pointer = stage?.getPointerPosition();
          const containerRect = stage?.container().getBoundingClientRect();
          if (!pointer || !containerRect) return;
          dispatch(
            setCoords({
              x: containerRect.left + pointer.x + 4,
              y: containerRect.top + pointer.y + 4,
            })
          );
          dispatch(openGroupContextMenu({ groupId: group.id }));
        };
        return (
          <Group
            key={group.id}
            x={bounds.x}
            y={bounds.y}
            draggable={!readOnly}
            onDragStart={() => {
              // Seed the synced-position tracker with the frame's
              // start origin so the first dragMove dispatches a delta
              // measured from there. Also flag the drag so the
              // expensive Parsons-condition evaluation skips its
              // recompute until the user releases.
              dragSyncRef.current = { x: bounds.x, y: bounds.y };
              dragOriginRef.current = { x: bounds.x, y: bounds.y };
              dispatch(startDragGroup({ groupId: group.id }));
            }}
            onDragMove={(e: Konva.KonvaEventObject<DragEvent>) => {
              const last = dragSyncRef.current;
              if (!last) return;
              const node = e.target;
              const cx = node.x();
              const cy = node.y();
              const dx = cx - last.x;
              const dy = cy - last.y;
              if (dx === 0 && dy === 0) return;
              dragSyncRef.current = { x: cx, y: cy };
              dispatch(
                translateGroupMembers({ groupId: group.id, dx, dy })
              );
            }}
            onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => {
              // Flush any residual delta from the last mouse position
              // (between the final dragMove and dragEnd Konva can fire
              // both, but a stray fractional pixel is still possible).
              const last = dragSyncRef.current;
              dragSyncRef.current = null;
              const origin = dragOriginRef.current;
              dragOriginRef.current = null;
              dispatch(endDragGroup());
              const node = e.target;
              if (last) {
                const dx = node.x() - last.x;
                const dy = node.y() - last.y;
                if (dx !== 0 || dy !== 0) {
                  dispatch(
                    translateGroupMembers({ groupId: group.id, dx, dy })
                  );
                }
              }

              // Drag-to-connect: dropping the frame onto a non-member
              // rect or another group's frame creates a group edge and
              // springs the group back to where the drag began — the
              // same gesture rect-to-rect linking already uses.
              if (!origin) return;
              const finalBounds: RectangleGeometry = {
                x: node.x(),
                y: node.y(),
                width: bounds.width,
                height: bounds.height,
              };
              const memberSet = new Set(group.memberRectIndices);
              const hitGroups = framed.filter(
                ({ group: other, bounds: otherBounds }) =>
                  other.id !== group.id &&
                  rectsHaveIntersection(finalBounds, otherBounds)
              );
              const hitGroupMembers = new Set(
                hitGroups.flatMap(({ group: g }) => g.memberRectIndices)
              );
              const hitRects: number[] = [];
              rectangles.forEach((r, i) => {
                if (memberSet.has(i)) return;
                // A rect inside a hit group is covered by the group
                // edge; don't also add a rect-level edge to it.
                if (hitGroupMembers.has(i)) return;
                if (rectsHaveIntersection(finalBounds, r)) hitRects.push(i);
              });
              if (hitGroups.length === 0 && hitRects.length === 0) return;
              for (const { group: other } of hitGroups) {
                dispatch(
                  addGroupEdge({
                    fromGroupId: group.id,
                    toGroupId: other.id,
                  })
                );
              }
              for (const idx of hitRects) {
                dispatch(
                  addGroupEdge({ fromGroupId: group.id, toRectIdx: idx })
                );
              }
              // Spring back to the drag origin.
              dispatch(
                translateGroupMembers({
                  groupId: group.id,
                  dx: origin.x - node.x(),
                  dy: origin.y - node.y(),
                })
              );
            }}
            onContextMenu={(e: Konva.KonvaEventObject<MouseEvent>) => {
              e.evt.preventDefault();
              openMenuAtPointer(e);
            }}
          >
            <Rect
              x={0}
              y={0}
              width={bounds.width}
              height={bounds.height}
              cornerRadius={FRAME_RADIUS}
              stroke={stroke}
              strokeWidth={1.5}
              dash={[6, 4]}
              fill={fill}
              listening
            />
            {/* Label pill. Listening so right-clicks on it bubble up to
                the frame's onContextMenu — the pill floats above the
                frame rect, so without a hit region of its own,
                right-clicks here would fall through to empty canvas. */}
            <Group x={8} y={-LABEL_HEIGHT - 4}>
              {(() => {
                // Inline IIFE so the toggle's extra width can be folded
                // into the pill width without duplicating the layout
                // calc above and below. Toggle only renders when the
                // canvas's evaluation_mode is on, so frames stay clean
                // when nobody's grading.
                const showToggle = evaluationMode && !readOnly;
                const isEvaluating = Boolean(group.evaluate);
                const labelText = group.name?.trim() || "Group";

                // Layout: [pad][name text][gap][checkbox][gap][Evaluate text][pad]
                const PAD = 12;
                const SECTION_GAP = 14; // gap between name and toggle
                const EVAL_TEXT_LABEL = "Evaluate";
                // Rough advance per char at 13px. Canvas-Text width is
                // expensive to measure exactly; this estimate is good
                // enough for pill sizing.
                const charAdvance = 7.2;
                const nameWidth = labelText.length * charAdvance;
                const evalLabelWidth = EVAL_TEXT_LABEL.length * charAdvance;

                const textX = PAD;
                const toggleX = textX + nameWidth + SECTION_GAP;
                const evalLabelX = toggleX + CHECKBOX_SIZE + CHECKBOX_LABEL_GAP;
                const evalLabelEndX = evalLabelX + evalLabelWidth;

                // Per-group result chip: only when the toggle is on.
                // Shows the issue count (red for cycles, orange
                // otherwise) or a green ✓ when evaluated and clean.
                const summary = groupSummaries.get(group.id);
                const showChip = showToggle && isEvaluating;
                const chipCount = summary?.count ?? 0;
                const chipIsClean = chipCount === 0;
                const chipText = chipIsClean ? "✓" : String(chipCount);
                const chipBg = chipIsClean
                  ? CHIP_OK_BG
                  : summary?.hasCycle
                    ? CHIP_ERROR_BG
                    : CHIP_WARN_BG;
                // Widen the chip a hair for multi-digit counts so the
                // pill doesn't crowd the number.
                const chipWidth = Math.max(
                  CHIP_MIN_WIDTH,
                  chipText.length * 8 + 10
                );
                const chipX = evalLabelEndX + CHIP_GAP;
                const chipY = (LABEL_HEIGHT - CHIP_HEIGHT) / 2;

                const pillWidth = showChip
                  ? chipX + chipWidth + PAD
                  : showToggle
                    ? evalLabelEndX + PAD
                    : textX + nameWidth + PAD;

                const textY = (LABEL_HEIGHT - LABEL_FONT_SIZE) / 2 - 1;
                const checkboxY = (LABEL_HEIGHT - CHECKBOX_SIZE) / 2;

                return (
                  <>
                    <Rect
                      x={0}
                      y={0}
                      width={pillWidth}
                      height={LABEL_HEIGHT}
                      cornerRadius={LABEL_HEIGHT / 2}
                      fill={labelBg}
                      stroke={stroke}
                      strokeWidth={1}
                    />
                    <Text
                      x={textX}
                      y={textY}
                      text={labelText}
                      fontSize={LABEL_FONT_SIZE}
                      fontStyle="600"
                      fill={labelTextFill}
                      listening={false}
                    />
                    {showToggle && (
                      <Group
                        onClick={(e: Konva.KonvaEventObject<MouseEvent>) => {
                          e.cancelBubble = true;
                          dispatch(
                            setGroupMeta({
                              groupId: group.id,
                              evaluate: !isEvaluating,
                            })
                          );
                        }}
                        onTap={(e: Konva.KonvaEventObject<Event>) => {
                          e.cancelBubble = true;
                          dispatch(
                            setGroupMeta({
                              groupId: group.id,
                              evaluate: !isEvaluating,
                            })
                          );
                        }}
                        onMouseEnter={(
                          e: Konva.KonvaEventObject<MouseEvent>
                        ) => {
                          const stage = e.target.getStage();
                          if (stage) stage.container().style.cursor = "pointer";
                        }}
                        onMouseLeave={(
                          e: Konva.KonvaEventObject<MouseEvent>
                        ) => {
                          const stage = e.target.getStage();
                          if (stage) stage.container().style.cursor = "default";
                        }}
                      >
                        {/* Invisible hit area covering checkbox + label
                            so the click target matches the visual
                            grouping. Keeps the affordance "Evaluate +
                            box are one button". */}
                        <Rect
                          x={toggleX - 4}
                          y={2}
                          width={
                            CHECKBOX_SIZE +
                            CHECKBOX_LABEL_GAP +
                            evalLabelWidth +
                            8
                          }
                          height={LABEL_HEIGHT - 4}
                          cornerRadius={4}
                          fill="transparent"
                        />
                        <Rect
                          x={toggleX}
                          y={checkboxY}
                          width={CHECKBOX_SIZE}
                          height={CHECKBOX_SIZE}
                          cornerRadius={3}
                          fill={isEvaluating ? TOGGLE_ON_FILL : labelBg}
                          stroke={
                            isEvaluating ? TOGGLE_ON_FILL : toggleOffStroke
                          }
                          strokeWidth={2}
                          listening={false}
                        />
                        {isEvaluating && (
                          <Text
                            x={toggleX + 2.5}
                            y={checkboxY + 1}
                            text="✓"
                            fontSize={CHECKBOX_SIZE - 2}
                            fontStyle="bold"
                            fill="#ffffff"
                            listening={false}
                          />
                        )}
                        <Text
                          x={evalLabelX}
                          y={textY}
                          text={EVAL_TEXT_LABEL}
                          fontSize={LABEL_FONT_SIZE - 1}
                          fill={labelTextFill}
                          listening={false}
                        />
                      </Group>
                    )}
                    {showChip && (
                      <Group listening={false}>
                        <Rect
                          x={chipX}
                          y={chipY}
                          width={chipWidth}
                          height={CHIP_HEIGHT}
                          cornerRadius={CHIP_HEIGHT / 2}
                          fill={chipBg}
                        />
                        <Text
                          x={chipX}
                          y={chipY + (CHIP_HEIGHT - 11) / 2 - 1}
                          width={chipWidth}
                          align="center"
                          text={chipText}
                          fontSize={11}
                          fontStyle="bold"
                          fill="#ffffff"
                        />
                      </Group>
                    )}
                  </>
                );
              })()}
            </Group>
          </Group>
        );
      })}
    </>
  );
};

export default CanvasGroupFrame;
